/* Copyright 2013 - 2024 Waiterio LLC */
import getBrowserLanguage from '@monorepool/shared/getBrowserLanguage.js'
import getPhrases from '@multilocale/client/getPhrases.js'

let defaultLanguage
let organizationId
let project
let dictionaries = {}
let loading
let error

export const init = params => {
  let cache = {}
  if (typeof window !== 'undefined' && window.__MULTILOCALE_CACHE__) {
    cache = window.__MULTILOCALE_CACHE__
  }

  // Server-side: each request may target a different project (one per
  // subdomain in a whitelabel setup). Reset module-level state so translations
  // from a previous request in the same warm Lambda don't leak.
  if (typeof window === 'undefined') {
    dictionaries = {}
    fetchedLanguages = {}
    suspender = undefined
  }

  defaultLanguage = params.defaultLanguage
  organizationId = params.organizationId
  project = params.project

  if (params.dictionaries) {
    dictionaries = params.dictionaries
  } else if (cache?.dictionaries && Object.keys(dictionaries).length === 0) {
    dictionaries = cache.dictionaries
  }
}

export const setDictionaries = dictionaries_ => {
  dictionaries = dictionaries_
}

export const getDefaultLanguage = () => defaultLanguage

export const setDefaultLanguage = defaultLanguage_ => {
  defaultLanguage = defaultLanguage_
}

export const initMultilocale = init

let suspender
let fetchedLanguages = {}

export const useTranslation = forcedLanguage => {
  let language =
    forcedLanguage || defaultLanguage || getBrowserLanguage() || 'en'

  let isBrowser = typeof window !== 'undefined'
  let isDictionaryEmpty =
    typeof dictionaries[language] === 'undefined' ||
    (isBrowser &&
      dictionaries[language] &&
      Object.keys(dictionaries[language]).length === 0)

  if (
    organizationId &&
    project &&
    !suspender &&
    isDictionaryEmpty &&
    !fetchedLanguages[language]
  ) {
    loading = true
    suspender = getPhrases(
      {
        organizationId,
        language,
        project,
        fields: ['key', 'value', 'language'],
      },
      {
        retries: 1,
      },
    )
      .then(phrases => {
        if (!dictionaries[language]) {
          dictionaries[language] = {}
        }

        phrases.forEach(phrase => {
          if (!dictionaries[phrase.language]) {
            dictionaries[phrase.language] = {}
          }
          dictionaries[phrase.language][phrase.key] = phrase.value
        })
        fetchedLanguages[language] = true
        suspender = null
        loading = false
      })
      .catch(error_ => {
        error = error_
        console.error(error)
        fetchedLanguages[language] = true
        suspender = null
        loading = false
        // THE SHOW MUST GO ON
        dictionaries[language] = {}
        return null
      })
  }

  if (loading) {
    throw suspender
    // THE SHOW MUST GO ON
    // } else if (error) {
    // throw error
  } else {
    const translateString = (key, fallback) => {
      let value = ''

      if (dictionaries?.[language]?.[key]) {
        value = dictionaries[language][key]
      } else if (fallback?.length >= 0) {
        value = fallback
      } else if (!(key?.startsWith('__') && key?.endsWith('__'))) {
        value = key
      }

      return value
    }

    const translateError = (error, fallback) => {
      let message = error?.toString?.()
      message = message?.length ? message : fallback || 'An error occurred'
      message = translateString(message, fallback)

      if (error?.variables) {
        Object.keys(error.variables || {}).forEach(variable => {
          message = message.replace(
            `{${variable}}`,
            translateString(error.variables[variable]),
          )
        })
      }

      message += error?.cause?.message
        ? `:\n${translateString(error.cause.message)}`
        : ''

      return message
    }

    const translate = (parameter1, fallback) => {
      if (parameter1 && parameter1 instanceof Error) {
        return translateError(parameter1, fallback)
      } else if (parameter1 && typeof parameter1 === 'string') {
        return translateString(parameter1, fallback)
      } else {
        return fallback
      }
    }

    const Cache = () => {
      const windowVariables = `window.__MULTILOCALE_CACHE__ = ${JSON.stringify({
        dictionaries: {
          [language]: dictionaries[language],
        },
      })};`
      return <script dangerouslySetInnerHTML={{ __html: windowVariables }} />
    }

    return {
      language,
      t: translate,
      translate,
      Cache,
      MultilocaleCache: Cache,
    }
  }
}

export default useTranslation
